import { useLoadJobs, type LoadCompletion } from '@/lib/loadJobs';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/Button';

/** Completion notices for background load jobs, fixed to the viewport so they
 *  appear whatever page the user navigated to while the load ran. The
 *  "in progress" side is `LoadBanBanner`; this is the result. */
export function LoadJobToasts() {
  const { completions, dismissCompletion } = useLoadJobs();
  if (completions.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2">
      {completions.map((c) => (
        <Toast key={c.id} completion={c} onDismiss={() => dismissCompletion(c.id)} />
      ))}
    </div>
  );
}

function Toast({
  completion,
  onDismiss,
}: {
  completion: LoadCompletion;
  onDismiss: () => void;
}) {
  const { ok, filename, graph, statements, error, detail, updates } = completion;
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-3 rounded-md border bg-card p-3 text-sm shadow-lg',
        ok ? 'border-tres-success/50' : 'border-tres-error/50',
      )}
    >
      <span
        className={cn(
          'mt-0.5 inline-block h-2 w-2 flex-shrink-0 rounded-full',
          ok ? 'bg-tres-success' : 'bg-tres-error',
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1 text-tres-text-primary">
        {ok ? (
          <>
            <p>
              Loaded <span className="font-medium break-all">{filename}</span>
              {statements !== undefined ? `: ${statements.toLocaleString()} statements` : ''} into{' '}
              <span className="font-mono break-all">{graph}</span>.
            </p>
            {updates && <p className="mt-1 text-xs text-tres-text-primary">{updates}</p>}
            {detail && (
              <p className="mt-1 whitespace-pre-wrap break-words text-xs text-tres-text-secondary">
                {detail}
              </p>
            )}
          </>
        ) : (
          <>
            <p>
              Load of <span className="font-medium break-all">{filename}</span> failed: {error}
            </p>
            {detail && (
              <p className="mt-1 whitespace-pre-wrap break-words text-xs text-tres-text-secondary">
                {detail}
              </p>
            )}
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'flex-shrink-0 p-0.5')}
      >
        ✕
      </button>
    </div>
  );
}
